/**
 * Fan download / videoboard S3 path segments.
 * Keep in sync with `client-operator/src/utils/downloadS3Path.ts` (copy on change).
 */

export function stripForDownloadSlug(s: string): string {
  return String(s || "").replace(/[()]/g, "").replace(/\s/g, "");
}

/** Legacy flat game folder: `Team1VsTeam2` after cleanup (matches historical uploads). */
export function buildTeamVsSlug(team1Name: string, team2Name: string): string {
  const a = stripForDownloadSlug(team1Name);
  const b = stripForDownloadSlug(team2Name);
  return `${a}Vs${b}`.replace(/[^a-zA-Z0-9_]/g, "_");
}

export function formatUtcDateYmd(ms: number): string {
  const d = new Date(ms);
  if (!Number.isFinite(d.getTime())) return "";
  const y = d.getUTCFullYear();
  const m = String(d.getUTCMonth() + 1).padStart(2, "0");
  const day = String(d.getUTCDate()).padStart(2, "0");
  return `${y}-${m}-${day}`;
}

export type DownloadGameSegmentInput = {
  team1Name?: string;
  team2Name?: string;
  gameStartDateMs?: number | null;
  gameId?: string | null;
};

/**
 * v2 game folder segment under stadium:
 * - `{YYYY-MM-DD}__{Team1VsTeam2}` when UTC date + both team names exist
 * - `_nodate__{Team1VsTeam2}` when teams exist but no reliable kickoff time
 * - `unknown_game__{sanitizedId}` when team names are missing (images only / incomplete context)
 */
export function buildDownloadGameSegment(input: DownloadGameSegmentInput): string {
  const t1 = input.team1Name?.trim();
  const t2 = input.team2Name?.trim();
  const rawMs = input.gameStartDateMs;
  const ms = rawMs != null && Number.isFinite(Number(rawMs)) ? Number(rawMs) : NaN;
  const dateStr = Number.isFinite(ms) && ms !== Number.MAX_SAFE_INTEGER
    ? formatUtcDateYmd(ms)
    : "";

  if (t1 && t2) {
    const slug = buildTeamVsSlug(t1, t2);
    if (dateStr) {
      return `${dateStr}__${slug}`;
    }
    return `_nodate__${slug}`;
  }

  const gid = (input.gameId && String(input.gameId).replace(/[^a-zA-Z0-9_]/g, "_").slice(0, 48))
    || "misc";
  return `unknown_game__${gid}`;
}

/** Dated v2 segment for an API game row, or null if we cannot build one reliably. */
export function buildDatedGameSegmentForOperator(game: {
  _id?: string;
  team1?: { name?: string };
  team2?: { name?: string };
  startDate?: number;
}): string | null {
  const t1 = (game.team1 as { name?: string } | undefined)?.name?.trim();
  const t2 = (game.team2 as { name?: string } | undefined)?.name?.trim();
  const ms = game.startDate;
  if (!t1 || !t2 || ms == null || !Number.isFinite(ms) || ms === Number.MAX_SAFE_INTEGER) {
    return null;
  }
  return buildDownloadGameSegment({
    team1Name: t1,
    team2Name: t2,
    gameStartDateMs: ms,
    gameId: game._id ?? null,
  });
}
